/* istanbul ignore file */
import { useState } from 'react';
import moment from 'moment';
import { MARK_AS_PAID_EXPENSE, useExpenseService } from 'domain/shared/expense';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { FormFieldsData, DateInput, TextInput } from 'framework/form';
import { useDateService, DATE_FORMAT, getVisibility } from 'framework/utils';
import { useShowContext } from 'framework/show';

export type MarkAsPaidExpenseButtonProps = {
  isVisible: boolean | ((data: any) => boolean);
  ids: string[];
  onSubmit?: () => void;
};

const MarkAsPaidExpenseButton = ({ isVisible, ids, onSubmit }: MarkAsPaidExpenseButtonProps) => {
  const { markAsPaidExpense, invalidateCache } = useExpenseService();
  const [payoutDateErrors, setPayoutDateErrors] = useState<string[]>([]);
  const { getToday, formatDateForAPI } = useDateService();
  const [payoutDate, setPayoutDate] = useState(getToday());
  const [bankTransactionId, setBankTransactionId] = useState<string>('');
  const { data, setData = () => {} } = useShowContext();

  if (!getVisibility(data, isVisible as any)) {
    return null;
  }

  const onPayoutDateChange = (values: FormFieldsData) => {
    setPayoutDate(values.payoutDate);
    const isValid = moment(values.payoutDate, DATE_FORMAT.API_ONLY_DATE, true).isValid();

    if (!values.payoutDate) {
      setPayoutDateErrors(['Field is required']);
    } else if (!isValid) {
      setPayoutDateErrors(['Please, choose the correct date']);
    } else {
      setPayoutDateErrors([]);
    }
  };

  const onBankTransactionIdCallback = (values: FormFieldsData) => {
    setBankTransactionId(values.bankTransactionId);
  };

  const execute = (newExpenseData?: any, shouldUpdateData?: boolean) => {
    invalidateCache();
    if (onSubmit) {
      onSubmit();
    } else if (shouldUpdateData && newExpenseData[0]) {
      setData(newExpenseData[0]);
    }
  };

  return (
    <ButtonWithConfirmation
      displayMessage={`Do you really want to mark ${
        ids.length > 1 ? `these expenses (ids: ${ids.join(', ')})` : `this expense (id: ${ids[0]})`
      } as paid? Please fill out the inputs below.`}
      modalChildren={
        <>
          <DateInput
            source="payoutDate"
            value={payoutDate as any}
            label="Payout date"
            onChangeCallback={onPayoutDateChange}
            errorMessages={payoutDateErrors}
            isRequired
            sx={{ mt: 2.5 }}
          />
          <TextInput
            source="bankTransactionId"
            label="Bank transaction ID"
            value={bankTransactionId}
            onChangeCallback={onBankTransactionIdCallback}
          />
        </>
      }
      submitMethod={() => markAsPaidExpense(ids, formatDateForAPI(payoutDate), bankTransactionId)}
      afterSubmit={{
        successMessage: `The Expense${ids.length > 1 ? 's have' : ' has'} been successfully marked as paid.`,
        errorMessage: `Can't mark expense${ids.length > 1 ? 's' : ''} as paid.`,
        execute: (newExpenseData: any) => execute(newExpenseData, true),
        executeOnError: execute,
      }}
      permissionToCheck={MARK_AS_PAID_EXPENSE}
      variant="outlined"
      confirmBtnProps={{ disabled: !!payoutDateErrors.length || !payoutDate }}
      closeOnSubmit={false}
    >
      Mark as paid
    </ButtonWithConfirmation>
  );
};

export default MarkAsPaidExpenseButton;
